import React from 'react';
import { VetValueFeeLedger } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { LevelOfService, isSfas } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { EM_DASH, formatMoney, formatPercent } from '../../../utils/flowOfFundsFormat';
import { FeeAgreementBlock } from '../FeeAgreementBlock/FeeAgreementBlock';
import './VetValueFees.scss';

type FeeRowKey =
    | 'transactionFeePercent'
    | 'projectedTransactionValue'
    | 'projectedTransactionFee'
    | 'applicationFee'
    | 'monthlyRetainerFees'
    | 'processFees'
    | 'lateDelayFees'
    | 'engagementSuspensionFees'
    | 'totalCredits'
    | 'netProjectedTotalFees'
    | 'portionPaidAtClose'
    | 'projectedDeferredPortion';

interface FeeRow {
    key: FeeRowKey;
    label: string;
    fee: string;
    credit: string;
    isSubRow?: boolean;
    isTotal?: boolean;
    hasTopGap?: boolean;
}

const blank = (value: number | null) => (value == null ? '' : formatMoney(value));

const SFAS_HIDDEN_ROWS = new Set<FeeRowKey>([
    'applicationFee',
    'processFees',
    'lateDelayFees',
    'engagementSuspensionFees',
]);

interface VetValueFeesProps {
    fees: VetValueFeeLedger;
    levelOfService: LevelOfService;
    onAgree?: () => void;
    isSaving?: boolean;
}

export const VetValueFees = ({ fees, levelOfService, onAgree, isSaving }: VetValueFeesProps) => {
    const isSfasEngagement = isSfas(levelOfService);
    const projectedDeferredPortion = fees.netProjectedTotalFees - fees.portionPaidAtClose;

    const rows: FeeRow[] = [
        {
            key: 'transactionFeePercent',
            label: isSfasEngagement ? 'Transaction Fee' : 'Transaction Fee %',
            fee: formatPercent(fees.transactionFeePercent),
            credit: '',
        },
        {
            key: 'projectedTransactionValue',
            label: 'Projected Transaction Value',
            fee: formatMoney(fees.projectedTransactionValue),
            credit: '',
        },
        {
            key: 'projectedTransactionFee',
            label: 'Projected Transaction Fee $',
            fee: formatMoney(fees.projectedTransactionFee),
            credit: '',
        },
        {
            key: 'applicationFee',
            label: fees.applicationFee.label,
            fee: blank(fees.applicationFee.fee),
            credit: blank(fees.applicationFee.credit),
        },
        {
            key: 'monthlyRetainerFees',
            label: fees.monthlyRetainerFees.label,
            fee: blank(fees.monthlyRetainerFees.fee),
            credit: blank(fees.monthlyRetainerFees.credit),
        },
        { key: 'processFees', label: 'Process Fees', fee: '', credit: '' },
        ...fees.processFees.map((item) => ({
            key: 'processFees' as FeeRowKey,
            label: item.label,
            fee: blank(item.fee),
            credit: blank(item.credit),
            isSubRow: true,
        })),
        {
            key: 'lateDelayFees',
            label: fees.lateDelayFees.label,
            fee: blank(fees.lateDelayFees.fee),
            credit: blank(fees.lateDelayFees.credit),
        },
        {
            key: 'engagementSuspensionFees',
            label: fees.engagementSuspensionFees.label,
            fee: blank(fees.engagementSuspensionFees.fee),
            credit: blank(fees.engagementSuspensionFees.credit),
        },
        {
            key: 'totalCredits',
            label: 'Total Credits',
            fee: `(${formatMoney(fees.totalCredits)})`,
            credit: formatMoney(fees.totalCredits),
            isTotal: true,
            hasTopGap: true,
        },
        {
            key: 'netProjectedTotalFees',
            label: 'Net Projected Total Fees',
            fee: formatMoney(fees.netProjectedTotalFees),
            credit: '',
            isTotal: true,
        },
        {
            key: 'portionPaidAtClose',
            label: 'Portion Paid at Close',
            fee: formatMoney(fees.portionPaidAtClose),
            credit: '',
            isTotal: true,
        },
    ];

    const visibleRows = isSfasEngagement
        ? rows
              .filter((row) => !SFAS_HIDDEN_ROWS.has(row.key))
              .concat({
                  key: 'projectedDeferredPortion',
                  label: 'Projected Deferred Portion',
                  fee: formatMoney(projectedDeferredPortion),
                  credit: '',
                  isTotal: true,
              })
        : rows;

    return (
        <div className="ff-vvfees">
            <div className="ff-vvfees__tickler-slot" />

            <div className="ff-vvfees__scroll">
                <table className="ff-vvfees__table">
                    <colgroup>
                        <col className="ff-vvfees__col-label" />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>VV Connect Fees</th>
                            <th>Fee</th>
                            <th>Credits</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row, index) => (
                            <tr
                                key={`${row.key}-${index}`}
                                className={[
                                    row.isSubRow ? 'is-sub' : '',
                                    row.isTotal ? 'is-total' : '',
                                    row.hasTopGap ? 'has-top-gap' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}>
                                <td>{row.label}</td>
                                <td>{row.fee || EM_DASH}</td>
                                <td>{row.credit || EM_DASH}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <FeeAgreementBlock
                agreement={fees.agreement}
                description="Both the Seller and VetValue must agree to these fees before they are finalized."
                onAgree={onAgree}
                isSaving={isSaving}
            />
        </div>
    );
};
